import { useContext, useState } from 'react'

import { digObject, HandleCallbackActionParams, toggleArray } from '@domain-group/javascript-utils'

import {
  Box, Button, Form, ModalContext, SidebarModal, Text,
} from '@domain-group/suit-theme'

import EntitySelector from '@components/EntitySelector'

import useSelector from '@hooks/useSelector'
import useUser from '@hooks/useUser'

import UserListItem from './UserListItem'

const MODAL_KEY = 'ManageEbuAssignedAgentsModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateUser: (payload: HandleCallbackActionParams) => Promise<object>,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

 interface RemoveUsersParams {
  userId: number,
  userIds: number[],
  setUserIds: (arg:any)=> void,
}

const removeAgentUsers = (params: RemoveUsersParams) => {
  const {
    userId,
    userIds,
    setUserIds,
  } = params

  const updatedIds = toggleArray(userIds, userId)

  setUserIds(updatedIds)
}

const ManageEbuAssignedAgentsModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateUser } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { user: initUser } = modalPayload

  const userPayload = useUser(initUser)

  const {
    callbacks: { updateUser: updateFn },
    updating,
    user,
  } = userPayload

  const {
    association_id, ebu_assigned_user_ids,
  } = user

  const [userIds, setUserIds] = useState(ebu_assigned_user_ids)

  const { users } = useSelector(reduxState => reduxState.entities)

  const updateUserPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    // @ts-expect-error TS7006
    entityParams: { id: user.id, assigned_agent_ids: userIds.map(id => users[id]?.actable_id) },
    requestOptions: {
      include_user_ebu_assigned_users: true,
      include_user_type: true,
    },
    toastText: 'User Updated',
  }

  return (
    <SidebarModal
      callbacks={callbacks}
      modalKey={MODAL_KEY}
      showModal={showModal}
      size="small"
    >
      <SidebarModal.Header callbacks={callbacks} title="Assign" titleSecondLine="Agents" />

      <SidebarModal.SubHeader boxProps={{ flexDirection: 'column' }}>
        <Text color="mysteryGrey" fontSize="small" width="100%">
          Assigning to User:
        </Text>

        <Box flexDirection="column" marginTop="large">
          <Text color="bodyFontColor" marginBottom="small">{user.full_name}</Text>

          <Text color="mysteryGrey" fontSize="small">EBU Admin</Text>
        </Box>
      </SidebarModal.SubHeader>

      {/*
       // @ts-expect-error TS2322 */}
      <SidebarModal.Content hasSubheader>
        {/* @ts-expect-error */}
        <EntitySelector
          callbacks={{
            selectItem: userAgent => setUserIds(
              (currentState: number[]) => [...currentState, userAgent?.id],
            ),

          }}
          entityKey="users"
          entityTitleKey="full_name"
          label="Search Agents"
          searchRequestOptions={{
            actable_types: ['Agent'],
            include_image_sizes: true,
            include_user_image: true,
          }}
          selectedIds={userIds}
          showSelectedEntity={false}
        />

        {userIds?.length > 0 && (
          <Form.Field labelProps={{ marginBottom: 'none' }} label="Assigned Agents" marginTop="large">
            {userIds?.map((userId: number) => (
              <UserListItem
                callbacks={{
                  removeAgentUsers: () => removeAgentUsers({
                    userId, userIds, setUserIds,
                  }),
                }}
                userId={userId}
              />
            ))}
          </Form.Field>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          onClick={() => updateUser(updateUserPayload)}
          size="large"
        >
          Save
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageEbuAssignedAgentsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
